import React, { useMemo, useState } from "react";
import { default as Chart, default as ReactApexChart } from "react-apexcharts";
import { useSelector } from "react-redux";

const chartStyle = {
  background: "#fff",
  borderRadius: "16px",
  padding: "20px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  flex: 1,
};

const chartContainer = {
  display: "flex",
  gap: "20px",
  marginTop: "20px",
};

const dropdownStyle = {
  padding: "5px 10px",
  border: "1px solid #ccc",
  borderRadius: "6px",
  fontSize: "14px",
};

const ageCategories = ["0-1", "1-3", "3-6", "6-9", "9-15", "15+", "Unknown"];
const reproCategories = ["Spayed", "Neutered", "Intact"];

function PieChartBox({
  title,
  labels,
  series,
  colors,
  percentages,
  petAvgApntList,
  petAvgBillPerCaseList,
  selectedDropdownValue,
  onDropdownChange,
  dropdownOptions,
}) {
  const options = {
    chart: {
      type: "donut",
    },
    labels: labels,
    legend: {
      show: false,
    },
    colors: colors,
    plotOptions: { pie: { donut: { size: "0%" } } },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const index = opts.seriesIndex;
        const value = opts.w.globals.series[index];
        let percentage;
        if (
          percentages &&
          percentages[index] !== null &&
          percentages[index] !== undefined
        ) {
          percentage = percentages[index];
        } else {
          const total = opts.w.globals.seriesTotals?.reduce((a, b) => a + b, 0);
          percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        }
        return `${value} (${percentage})`;
      },
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    stroke: {
      width: 0,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, w }) {
        const label = w.globals.labels[seriesIndex];
        const value = series[seriesIndex];
        let percentage;
        if (
          percentages &&
          percentages[seriesIndex] !== null &&
          percentages[seriesIndex] !== undefined
        ) {
          percentage = percentages[seriesIndex];
        } else {
          const total = series.reduce((a, b) => a + b, 0);
          percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        }

        const avgAppts =
          petAvgApntList && petAvgApntList[seriesIndex] !== undefined
            ? petAvgApntList[seriesIndex]
            : null;
        const avgBill =
          petAvgBillPerCaseList &&
          petAvgBillPerCaseList[seriesIndex] !== undefined
            ? petAvgBillPerCaseList[seriesIndex]
            : null;

        let tooltipContent =
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          label +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          w.globals.colors[seriesIndex] +
          '; margin-left: 5px;"></span>' +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          // '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          // w.globals.colors[seriesIndex] +
          // ';"></span>' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          value +
          " (" +
          percentage +
          ")</span>" +
          "</div>";

        if (avgAppts !== null) {
          tooltipContent +=
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Average Appts per Pet: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            avgAppts +
            "</span>" +
            "</div>";
        }

        if (avgBill !== null) {
          tooltipContent +=
            '<div class="apexcharts-tooltip-y-group">' +
            '<span class="apexcharts-tooltip-text-y-label">Average Bill per Case: </span>' +
            '<span class="apexcharts-tooltip-text-y-value">' +
            avgBill +
            "</span>" +
            "</div>";
        }

        tooltipContent += "</div></div></div>";

        return tooltipContent;
      },
    },
  };

  const labelStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "6px",
    fontSize: "14px",
  };

  return (
    <div style={chartStyle}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>{title}</h3>
        {selectedDropdownValue && onDropdownChange && dropdownOptions && (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <select
              value={selectedDropdownValue}
              onChange={onDropdownChange}
              style={dropdownStyle}
            >
              {dropdownOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <hr />
      <div style={{ display: "flex" }}>
        <div style={{ width: "50%" }}>
          <Chart options={options} series={series} type="donut" width="100%" />
        </div>
        <div
          style={{
            width: "50%",
            paddingLeft: "10px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {labels?.map((label, index) => {
            return (
              <div key={index} style={labelStyle}>
                <div
                  style={{ display: "flex", gap: "6px", alignItems: "center" }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: colors[index],
                      display: "inline-block",
                    }}
                  ></span>
                  {label}
                </div>
                <div>{series[index]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function AgeAndReproductiveCharts() {
  const { dashboardData } = useSelector((state) => state.dashboard);
  const [selectedSpeciesAge, setSelectedSpeciesAge] = useState("Dog");
  const [selectedSpeciesPetType, setSelectedSpeciesPetType] = useState("Dog");
  const [selectedSpeciesRepro, setSelectedSpeciesRepro] = useState("Dog");

  // Age Chart Series
  const ageSeries = useMemo(() => {
    if (!dashboardData?.reproductiveStatus) return [];

    const speciesData = dashboardData.reproductiveStatus;
    const categories = Object.keys(speciesData);
    const reqData = categories?.map((li) => {
      const suCategories = Object.keys(speciesData[li]);
      const reqSubCatData = suCategories?.map((sc) => {
        return (
          (speciesData?.[li]?.[sc]?.spayed ?? 0) +
          (speciesData?.[li]?.[sc]?.neutered ?? 0) +
          (speciesData?.[li]?.[sc]?.intact ?? 0)
        );
        // {
        //   ...speciesData[li][sc],
        //   count:
        //     (speciesData?.[li]?.[sc]?.spayed ?? 0) +
        //     (speciesData?.[li]?.[sc]?.neutered ?? 0) +
        //     (speciesData?.[li]?.[sc]?.intact ?? 0),
        // };
      });
      return { name: li, data: reqSubCatData };
    });
    // [selectedSpeciesAge] || {};
    // const maleData = ageCategories.map((age) => speciesData.male?.[age] || 0);
    // const femaleData = ageCategories.map(
    //   (age) => speciesData.female?.[age] || 0
    // );
    return reqData;
    // [
    //   { name: "Male", data: maleData },
    //   { name: "Female", data: femaleData },
    // ];
  }, [dashboardData, selectedSpeciesAge]);

  const ageSeriesForHover = useMemo(() => {
    if (!dashboardData?.reproductiveStatus) return [];

    const speciesData = dashboardData.reproductiveStatus;
    const categories = Object.keys(speciesData);
    const reqData = categories?.map((li) => {
      const suCategories = Object.keys(speciesData[li]);
      const reqSubCatData = suCategories?.map((sc) => {
        return {
          ...speciesData[li][sc],
          count:
            (speciesData?.[li]?.[sc]?.spayed ?? 0) +
            (speciesData?.[li]?.[sc]?.neutered ?? 0) +
            (speciesData?.[li]?.[sc]?.intact ?? 0),
        };
      });
      return { name: li, data: reqSubCatData };
    });
    return reqData;
  }, [dashboardData, selectedSpeciesAge]);

  // Prepare reproductive status data by age and gender
  const reproductiveStatusByAge = useMemo(() => {
    if (!dashboardData?.age) return {};

    const speciesData = dashboardData.age[selectedSpeciesAge] || {};
    const result = {};

    ageCategories.forEach((age) => {
      result[age] = {
        male: {
          spayed: speciesData.male?.[age]?.spayed || 0,
          neutered: speciesData.male?.[age]?.neutered || 0,
          intact: speciesData.male?.[age]?.intact || 0,
        },
        female: {
          spayed: speciesData.female?.[age]?.spayed || 0,
          neutered: speciesData.female?.[age]?.neutered || 0,
          intact: speciesData.female?.[age]?.intact || 0,
        },
      };
    });

    return result;
  }, [dashboardData, selectedSpeciesAge]);

  const ageOptions = {
    chart: { type: "bar", stacked: true, toolbar: { show: false } },
    plotOptions: {
      bar: { horizontal: false, columnWidth: "40%", borderRadius: 10 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontWeight: "bold", colors: ["#fff"] },
    },
    colors: ["#1E90FF", "#FF69B4", "#dee6ff"],
    xaxis: { categories: ageCategories },
    legend: { position: "top", horizontalAlign: "left" },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const ageCategory = ageCategories[dataPointIndex];
        const gender =
          seriesIndex === 0 ? "male" : seriesIndex === 1 ? "female" : "unknown";
        const value = series[seriesIndex][dataPointIndex];
        const reproData = ageSeriesForHover[seriesIndex]?.data?.[
          dataPointIndex
        ] || {
          spayed: 0,
          neutered: 0,
          intact: 0,
        };
        return (
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          "Age: " +
          ageCategory +
          ", " +
          (gender === "male"
            ? "Male"
            : gender === "female"
            ? "Female"
            : "Unknown") +
          '<span class="apexcharts-tooltip-marker" style="background-color: ' +
          w.globals.colors[seriesIndex] +
          '; margin-left: 5px;"></span>' +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Total: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          value +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Spayed: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          reproData.spayed +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Neutered: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          reproData.neutered +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Intact: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          reproData.intact +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
  };

  // Reproductive Chart Series
  const reproductiveSeries = useMemo(() => {
    if (!dashboardData?.reproductiveStatus) return [];

    const speciesKey = selectedSpeciesRepro.toLowerCase(); // 'dog', 'cat'
    const reproductiveList = dashboardData.reproductiveStatus[speciesKey] || [];
    const reproductiveData = reproductiveList[0] || {};

    return [
      {
        name: "Spayed",
        data: reproCategories.map((cat) =>
          cat === "Spayed" ? reproductiveData["Spayed"] || 0 : 0
        ),
      },
      {
        name: "Neutered",
        data: reproCategories.map((cat) =>
          cat === "Neutered" ? reproductiveData["Neutered"] || 0 : 0
        ),
      },
      {
        name: "Intact",
        data: reproCategories.map((cat) =>
          cat === "Intact" ? reproductiveData["Intact"] || 0 : 0
        ),
      },
    ];
  }, [dashboardData, selectedSpeciesRepro]);

  const reproductiveOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: { columnWidth: "40%", borderRadius: 10 },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "13px", fontWeight: "bold", colors: ["#fff"] },
    },
    colors: ["#1E90FF", "#FF69B4", "#32CD32"],
    xaxis: { categories: reproCategories },
    legend: { position: "top", horizontalAlign: "left" },
  };

  if (!dashboardData) return null;

  const petTypeSeries = [
    dashboardData?.ownershipType?.Household?.count ?? 0,
    dashboardData?.ownershipType?.Rescue?.count ?? 0,
  ];
  const petTypePercentages = [
    dashboardData?.ownershipType?.Household?.percentage ?? 0,
    dashboardData?.ownershipType?.Rescue?.percentage ?? 0,
  ];
  const petAvgApntList = [
    dashboardData?.ownershipType?.Household?.averageAppointmentsPerPet ?? 0,
    dashboardData?.ownershipType?.Rescue?.averageAppointmentsPerPet ?? 0,
  ];
  const petAvgBillPerCaseList = [
    dashboardData?.ownershipType?.Household?.averageBillPerCase ?? 0,
    dashboardData?.ownershipType?.Rescue?.averageBillPerCase ?? 0,
  ];

  const petTypeChart = {
    title: "Household vs Stray/NGO ",
    labels: ["Household", "Stray/NGO"],
    series: petTypeSeries,
    colors: ["#1E90FF", "#FF69B4"],
    percentages: petTypePercentages,
    petAvgApntList,
    petAvgBillPerCaseList,
  };

  return (
    <div style={chartContainer}>
      {/* Age Chart */}
      <div style={chartStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3 style={{ margin: 0 }}>
            Age Distribution by Gender & Reproductive Status
          </h3>
          <select
            value={selectedSpeciesAge}
            onChange={(e) => setSelectedSpeciesAge(e.target.value)}
            style={dropdownStyle}
          >
            <option value="Dog">Dog</option>
            <option value="Cat">Cat</option>
          </select>
        </div>
        <hr />
        <ReactApexChart
          options={ageOptions}
          series={ageSeries}
          type="bar"
          height={300}
        />
      </div>

      <PieChartBox
        {...petTypeChart}
        selectedDropdownValue={selectedSpeciesPetType}
        onDropdownChange={(e) => setSelectedSpeciesPetType(e.target.value)}
        dropdownOptions={[
          { value: "Dog", label: "Dog" },
          { value: "Cat", label: "Cat" },
        ]}
      />

      {/* Reproductive Chart */}
      {/* <div style={chartStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0 }}>Reproductive Status</h4>
          <select
            value={selectedSpeciesRepro}
            onChange={(e) => setSelectedSpeciesRepro(e.target.value)}
            style={dropdownStyle}
          >
            <option value="Dog">Dog</option>
            <option value="Cat">Cat</option>
          </select>
        </div>
        <ReactApexChart options={reproductiveOptions} series={reproductiveSeries} type="bar" height={300} />
      </div> */}
    </div>
  );
}
